import { useAuth } from 'react-oidc-context';
import { useCallback } from 'react';
import { restartAuthentication } from './authSessionRecovery';

export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export interface HttpResponse {
  response: Promise<Response>;
  abort: () => void;
}

export type Http = (input: RequestInfo | URL, init?: RequestInit) => HttpResponse;

export const useHttp = (): Http => {
  const auth = useAuth();

  return useCallback((input: RequestInfo | URL, init: RequestInit = {}): HttpResponse => {
    const controller = new AbortController();
    const headers = new Headers(init.headers);

    if (auth.user?.access_token) {
      headers.set('Authorization', `Bearer ${auth.user.access_token}`);
    }

    const response = fetch(input, {
      ...init,
      headers,
      signal: controller.signal
    }).then(async res => {
      if (res.status === 401) {
        void restartAuthentication(auth).catch(() => undefined);
        throw new HttpError(res.status, 'Unauthorized');
      }

      if (res.status === 403) {
        throw new HttpError(res.status, 'Forbidden');
      }

      return res;
    });

    return {
      response,
      abort: () => controller.abort()
    };
  }, [auth]);
};

export default useHttp;
